import React, { useState } from 'react';
import { 
  Bell, 
  User, 
  Menu,
  LayoutDashboard,
  Package,
  Layers,
  ShieldCheck,
  Wallet,
  CreditCard,
  Settings,
  LogOut
} from 'lucide-react';
import { useSession } from '../../contexts/SessionContext';
import { useNotifications } from '../../contexts/NotificationContext';
import './Navbar.css';

const Navbar = ({ activeTab, setActiveTab, userEmail, onToggleSidebar, sidebarCollapsed, children }) => {
  const { logout } = useSession();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'inventory', label: 'Inventory', icon: Layers },
    { id: 'kyc', label: 'KYC', icon: ShieldCheck },
    { id: 'payouts', label: 'Payouts', icon: Wallet },
    { id: 'bank-details', label: 'Bank', icon: CreditCard },
    { id: 'profile', label: 'Settings', icon: Settings },
  ];

  const handleNotificationClick = (notification) => {
    if (notification.unread) {
      markAsRead(notification.id);
    }
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
    setShowNotifications(false);
  };

  return (
    <header className="navbar">
      <div className="navbar-content">
        <div className="navbar-left">
          <button className="mobile-menu-btn" onClick={onToggleSidebar} aria-label={sidebarCollapsed ? 'Open menu' : 'Close menu'}>
            <Menu size={24} />
          </button>

          <div className="brand" aria-label="Brand">
            <div className="brand-logo" aria-hidden="true">
              <img
                className="brand-img"
                src={`${process.env.PUBLIC_URL || ''}/sonickart1.jpg`}
                alt="SoniCKart"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <span className="brand-text">SonicKart</span>
          </div>
        </div>

        <div className="navbar-center" role="navigation" aria-label="Primary">
          <nav className="navbar-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`nav-icon-btn ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(item.id)}
                  title={item.label}
                >
                  <Icon size={20} />
                </button>
              );
            })}
          </nav>
        </div>

        <div className="navbar-right">
          <div className="notification-container">
            <button 
              className="notification-btn"
              onClick={() => setShowNotifications(!showNotifications)}
              aria-label="Toggle notifications"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </button>
            
            {showNotifications && (
              <div className="notification-dropdown">
                <div className="notification-header">
                  <h3>Notifications</h3>
                  {notifications.length > 0 && unreadCount > 0 && (
                    <button 
                      className="mark-all-read"
                      onClick={handleMarkAllRead}
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="notification-list">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div 
                        key={notification.id} 
                        className={`notification-item ${notification.unread ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="notification-content">
                          <p className="notification-message">{notification.message}</p>
                          <span className="notification-time">{notification.time}</span>
                        </div>
                        {notification.unread && <div className="unread-dot"></div>}
                      </div>
                    ))
                  ) : (
                    <div className="no-notifications">
                      <p>No notifications</p>
                      <span>You're all caught up!</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Session Status Component */}
          {children}

          <div className="user-profile-container">
            <button 
              className="user-profile"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="Toggle profile menu"
            >
              <div className="user-avatar">
                <User size={20} />
              </div>
              <div className="user-info">
                <span className="user-name">{userEmail || 'Vendor User'}</span>
                <span className="user-role">Vendor</span>
              </div>
            </button>
            
            {showUserMenu && (
              <div className="user-menu">
                <div className="user-menu-header">
                  <div className="user-avatar-large">
                    <User size={24} />
                  </div>
                  <div className="user-details">
                    <div className="user-name">{userEmail || 'Vendor User'}</div>
                    <div className="user-email">Vendor</div>
                  </div>
                </div>
                <div className="user-menu-divider"></div>
                <button className="user-menu-item" onClick={logout}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <nav className="navbar-mobile-nav" aria-label="Section shortcuts">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={`mobile-${item.id}`}
              className={`mobile-nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => setActiveTab(item.id)}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </header>
  );
};

export default Navbar;
